import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiFetch } from '@/lib/apiClient';
import { restaurantKeys, SearchParams, SearchLocationParams, RestaurantAutocompleteParams } from '@/api/keys';
import { useAppDispatch } from '@/store/store';
import { selectIsLoggedIn } from '@/store/slices/authSlice';
import { setGeometry } from '@/store/slices/locationSlice';
import { handleSearchResponse, setIsSearchLoading } from '@/store/slices/searchSlice';
import { LocationDetails } from '@/types/locationDetails';
import { Restaurant } from '@/types/restaurant';
import {
  CheckPendingImagesResponse,
  RestaurantRatingsResponse,
  SearchLocationResponse,
  SearchResponse,
} from '@/types/server/restaurantApiResponses';
import { useRingConsumption } from './useRingConsumption';
import { retryUnlessClientError } from './restaurantDetailRetry';
import { SEARCH_REQUEST_TIMEOUT_MS, withTimeout } from './searchRequest';

export { getExpandedSearchResults } from './searchRequest';

// Re-export types from restaurantApi for backward compatibility
export interface RestaurantSearchResult {
  id: number;
  name: string;
  address: string;
  /** City the restaurant is located in, when known. */
  city?: string | null;
  coverPhoto: string | null;
  /**
   * TasteMatch-style personal rating on the signed -100..100 internal
   * scale — never a 0-100 recommendation score. See
   * `src/utils/tasteMatchBand.ts`, which bands this value directly via
   * `getCompatibilityLevelFromInternalRating` (never through
   * `getInternalRatingFromRecommendationScore`).
   */
  rating: number | null;
  googleRating: number | null;
  /** Primary cuisine label returned by the search endpoint. */
  cuisineLabel?: string | null;
  /** Distance from the biasing lat/lng, in meters, when location was supplied. */
  distanceMeters?: number | null;
  /**
   * Whether this row carries a real personal score (of either
   * `scoreSource` below) — the purpose-built gate for whether a TasteMatch
   * chip renders at all. Distinct from `hasPersonalRating()`
   * (restaurantUtils.tsx), which derives the same fact from `rating`/
   * `scoreSource` on the full `Restaurant` type; the two are equivalent,
   * not competing.
   */
  hasScore?: boolean;
  /** Which mechanism produced `rating`, when `hasScore` is true. */
  scoreSource?: 'taste_match' | 'self_rating' | 'none';
}

export interface RestaurantSearchResponse {
  restaurants: RestaurantSearchResult[];
  totalResults: number;
}

export interface HistoryResponse {
  restaurants: Restaurant[];
  total_restaurants: number;
  performance: {
    duration_ms: number;
    user_id: number;
  };
}

/**
 * Hook to search restaurants by current position
 * Includes Redux side effects to update search state
 */
export function useSearch(params: SearchParams, options?: { enabled?: boolean }) {
  const dispatch = useAppDispatch();
  const enabled = options?.enabled ?? (params.position.lat !== 0 && params.position.lng !== 0);

  const query = useQuery({
    queryKey: restaurantKeys.search(params),
    queryFn: () =>
      withTimeout(
        apiFetch<SearchResponse>('/restaurants/search/', {
          params: {
            position: JSON.stringify(params.position),
            searchTerm: params.searchTerm,
            placeType: params.placeType,
            pages: params.pages,
            openNow: params.openNow ?? false,
          },
        }),
        SEARCH_REQUEST_TIMEOUT_MS
      ),
    enabled,
    retry: 2,
  });

  // Side effect: Update loading state
  useEffect(() => {
    dispatch(setIsSearchLoading(query.isFetching));
  }, [query.isFetching, dispatch]);

  // Side effect: Update search response in Redux
  useEffect(() => {
    if (query.data) {
      // Build location string from city/state if available
      const { city, state } = query.data.locationDetails || {};
      const location = city && state ? `${city}, ${state}` : city || state || '';

      const locationDetails: LocationDetails = {
        ...query.data.locationDetails,
        location,
        originalLocation: true,
        latitude: query.data.position.lat,
        longitude: query.data.position.lng,
      };
      dispatch(handleSearchResponse(locationDetails));
    }
  }, [query.data, dispatch]);

  return query;
}

/**
 * Hook to search restaurants by location place ID
 * Includes Redux side effects to update search and location state
 */
export function useSearchLocation(params: SearchLocationParams, options?: { enabled?: boolean }) {
  const dispatch = useAppDispatch();
  const enabled = options?.enabled ?? !!params.locationPlaceId;
  const queryKey = restaurantKeys.searchByLocation(params);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      withTimeout(
        apiFetch<SearchLocationResponse>(`/restaurants/search/${params.locationPlaceId}/`, {
          params: {
            searchTerm: params.searchTerm,
            placeType: params.placeType,
            pages: params.pages,
            openNow: params.openNow ?? false,
          },
        }),
        SEARCH_REQUEST_TIMEOUT_MS
      ),
    enabled,
    retry: 2,
  });

  // CR-8: location-search consumes ring expansion through the same one
  // implementation current-location search uses. The backend has always sent
  // this mode the progressive metadata; only the consumer was missing.
  const ringConsumption = useRingConsumption({
    searchId: query.data?.searchId ?? null,
    hasMoreRings: query.data?.hasMoreRings ?? false,
    searchGeneration: query.data?.searchGeneration ?? null,
    queryKey,
    data: query.data,
    enabled,
  });

  // Side effect: Update loading state (only when enabled — disabled hooks must not reset loading state)
  useEffect(() => {
    if (enabled) dispatch(setIsSearchLoading(query.isFetching));
  }, [query.isFetching, enabled, dispatch]);

  // Side effect: Update search response and geometry in Redux
  useEffect(() => {
    if (query.data) {
      // Build location string from city/state if not already provided
      const { city, state, location: existingLocation } = query.data.locationDetails || {};
      const location = existingLocation || (city && state ? `${city}, ${state}` : city || state || '');

      const locationDetails: LocationDetails = {
        ...query.data.locationDetails,
        placeId: params.locationPlaceId, // Use the placeId from params since backend doesn't return it
        location,
        originalLocation: false, // This is a searched location, not current location
        latitude: query.data.position.lat,
        longitude: query.data.position.lng,
      };
      dispatch(handleSearchResponse(locationDetails));
      dispatch(setGeometry(query.data.position));
    }
  }, [query.data, params.locationPlaceId, dispatch]);

  return {
    ...query,
    searchId: query.data?.searchId ?? null,
    hasMoreRings: ringConsumption.hasMoreRings,
    isRealtimeActive: ringConsumption.isRealtimeActive,
  };
}

/**
 * Fetches a restaurant's summary. Shared by `useGetRestaurantDetails` and the
 * restaurant route loader so both hit the same URL. The trailing slash is
 * deliberate: Django 301-redirects the slashless form, costing an extra
 * preflight + redirect round trip per restaurant.
 *
 * `signal` is TanStack Query's abort signal: passing it through lets
 * `cancelQueries` (e.g. a save while the profile refresh is in flight) abort
 * the HTTP request instead of only discarding the response.
 */
export function fetchRestaurantSummary(
  restaurantId: number,
  options?: { signal?: AbortSignal }
) {
  return apiFetch<Restaurant>(`/restaurants/${restaurantId}/summary/`, {
    signal: options?.signal,
  });
}

/**
 * Hook to get restaurant details by ID
 */
export function useGetRestaurantDetails(
  restaurantId: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: restaurantKeys.detail(restaurantId),
    queryFn: ({ signal }) => fetchRestaurantSummary(restaurantId, { signal }),
    enabled: options?.enabled ?? !!restaurantId,
    // A 4xx (404 on a seeded profile) redirects promptly instead of after retries.
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to get restaurant ratings for current user
 */
export function useGetRestaurantRatings(
  restaurantId: number,
  options?: { enabled?: boolean }
) {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  return useQuery({
    queryKey: restaurantKeys.ratings(restaurantId),
    queryFn: () =>
      apiFetch<RestaurantRatingsResponse>(`/restaurants/${restaurantId}/ratings/`),
    // Only fetch ratings for authenticated users
    enabled: (options?.enabled ?? !!restaurantId) && isLoggedIn,
  });
}

/**
 * Hook to search/autocomplete restaurants
 */
export function useSearchRestaurants(
  params: RestaurantAutocompleteParams,
  options?: { enabled?: boolean }
) {
  const hasValidSearch =
    (params.q?.length ?? 0) >= 2 ||
    (params.name?.length ?? 0) >= 2;

  return useQuery({
    queryKey: restaurantKeys.autocomplete(params),
    queryFn: ({ signal }) =>
      apiFetch<RestaurantSearchResponse>('/restaurants/autocomplete/', {
        params: {
          ...(params.q && { q: params.q }),
          ...(params.name && { name: params.name }),
          limit: params.limit ?? 10,
          // Location bias: include coords when available so the backend can
          // rank nearby restaurants higher. Falls back to unbiased results
          // when coordinates are not provided (permission denied / web).
          ...(params.lat !== undefined && params.lng !== undefined && {
            lat: params.lat,
            lng: params.lng,
          }),
        },
        // TanStack Query supplies this AbortSignal and aborts it once this
        // query loses its last active observer (unmount, or the component
        // switching to a different query key) — threaded into apiFetch ->
        // axios so the abandoned connection is actually released, not left
        // running to completion for nothing. This is NOT what protects a
        // stale response from overwriting a newer result set — that
        // guarantee comes from TanStack's per-query-key cache isolation
        // (a superseded query's response lands in its own cache entry,
        // never the one the component is currently reading).
        signal,
      }),
    enabled: (options?.enabled ?? true) && hasValidSearch,
  });
}

/**
 * Hook to get user's restaurant browsing history
 */
export function useGetHistory() {
  return useQuery({
    queryKey: restaurantKeys.history(),
    queryFn: () => apiFetch<HistoryResponse>('/restaurants/history/'),
  });
}

/**
 * Hook to poll for pending restaurant image downloads
 * After a search, images are downloaded in the background - this polls until complete
 * Polls every 5 seconds while there are pending restaurants
 */
export function usePendingRestaurantImages(pendingRestaurantIds: number[]) {
  const hasPending = pendingRestaurantIds.length > 0;

  return useQuery({
    queryKey: restaurantKeys.pendingImages(pendingRestaurantIds),
    queryFn: () =>
      apiFetch<CheckPendingImagesResponse>('/restaurants/check-pending-images/', {
        method: 'POST',
        body: { restaurantIds: pendingRestaurantIds },
      }),
    enabled: hasPending,
    refetchInterval: hasPending ? 5000 : false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook to search restaurants with real-time H3 ring expansion updates.
 *
 * Initial search returns center hex only for fast response.
 * Background tasks expand through k=1, k=2, k=3 rings.
 * Ably signals notify when each ring completes - results are appended automatically.
 *
 * @example
 * ```tsx
 * const { data, isLoading, hasMoreRings } = useSearchWithRealTimeUpdates(params);
 *
 * // data.restaurants grows as rings complete
 * // hasMoreRings shows if more results are loading
 * ```
 */
export function useSearchWithRealTimeUpdates(
  params: SearchParams,
  options?: { enabled?: boolean }
) {
  const dispatch = useAppDispatch();

  const enabled = options?.enabled ?? (params.position.lat !== 0 && params.position.lng !== 0);
  const queryKey = restaurantKeys.search(params);

  // Initial search - returns center hex only
  const query = useQuery({
    queryKey,
    queryFn: () =>
      withTimeout(
        apiFetch<SearchResponse>('/restaurants/search/', {
          params: {
            position: JSON.stringify(params.position),
            searchTerm: params.searchTerm,
            placeType: params.placeType,
            pages: params.pages,
            openNow: params.openNow ?? false,
          },
        }),
        SEARCH_REQUEST_TIMEOUT_MS
      ),
    enabled,
    retry: 2,
  });

  // Search identity is read off the cached envelope rather than captured by a
  // setState inside the queryFn — a query served from cache never runs its
  // queryFn, which used to leave `searchId` null and silently disable ring
  // consumption for the whole search.
  const searchId = query.data?.searchId ?? null;

  const ringConsumption = useRingConsumption({
    searchId,
    hasMoreRings: query.data?.hasMoreRings ?? false,
    searchGeneration: query.data?.searchGeneration ?? null,
    queryKey,
    data: query.data,
    enabled,
  });

  // Side effect: Update loading state (only when enabled — disabled hooks must not reset loading state)
  useEffect(() => {
    if (enabled) dispatch(setIsSearchLoading(query.isFetching));
  }, [query.isFetching, enabled, dispatch]);

  // Side effect: Update search response in Redux
  useEffect(() => {
    if (query.data) {
      const { city, state } = query.data.locationDetails || {};
      const location = city && state ? `${city}, ${state}` : city || state || '';

      const locationDetails: LocationDetails = {
        ...query.data.locationDetails,
        location,
        originalLocation: true,
        latitude: query.data.position.lat,
        longitude: query.data.position.lng,
      };
      dispatch(handleSearchResponse(locationDetails));
    }
  }, [query.data, dispatch]);

  return {
    ...query,
    searchId,
    hasMoreRings: ringConsumption.hasMoreRings,
    isRealtimeActive: ringConsumption.isRealtimeActive,
  };
}

